/**
 * Space-Entity IDB persistence.
 *
 * Per-space JSON snapshots of the entities returned by the two-stage fetch in
 * spaceEntityStore. One IndexedDB database per space, named:
 *
 *   river__space-entities__{spaceId}
 *
 * Schema:
 *   meta:     keyPath 'k', single record at k='meta' →
 *             { etag, fetchedAt, entityCount, dataspaceId, schemaVersion }
 *   entities: keyPath '_id', stripped entities (same projection as REST returns)
 *
 * The cache is a soft acceleration cache: on every space open we paint from
 * IDB immediately and kick off a REST refresh in the background (stale-while-
 * revalidate). Storage pressure / age eviction (storageMonitor.ts) reclaims
 * databases that haven't been touched recently.
 */

import { buildIDBKey } from './idbKeyManager';
import { idbManifest } from './idbManifest';
import { deleteIDBDatabase } from './idbUtils';
import { createFileLogger } from '../../utils/logger';
import type { BaseEntityType } from '../../types/entities';

const log = createFileLogger('SPACE_ENTITY_IDB');

const SCHEMA_VERSION = 1;
const DB_VERSION = 1;
const META_STORE = 'meta';
const ENTITIES_STORE = 'entities';
const META_KEY = 'meta';

export interface SpaceEntitiesMeta {
  k: typeof META_KEY;
  etag: string | null;
  fetchedAt: number;
  entityCount: number;
  dataspaceId: string | null;
  schemaVersion: number;
}

export interface SpaceEntitiesSnapshot {
  meta: SpaceEntitiesMeta;
  entities: BaseEntityType[];
}

function dbName(spaceId: string): string {
  return buildIDBKey('space-entities', spaceId);
}

function openDB(spaceId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(spaceId), DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'k' });
      }
      if (!db.objectStoreNames.contains(ENTITIES_STORE)) {
        db.createObjectStore(ENTITIES_STORE, { keyPath: '_id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function awaitReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Read the cached snapshot for a space. Returns null if no cache exists.
 */
export async function readSnapshot(spaceId: string): Promise<SpaceEntitiesSnapshot | null> {
  if (!spaceId) return null;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(spaceId);
    const tx = db.transaction([META_STORE, ENTITIES_STORE], 'readonly');
    const metaStore = tx.objectStore(META_STORE);
    const entitiesStore = tx.objectStore(ENTITIES_STORE);

    const [meta, entities] = await Promise.all([
      awaitReq(metaStore.get(META_KEY)) as Promise<SpaceEntitiesMeta | undefined>,
      awaitReq(entitiesStore.getAll()) as Promise<BaseEntityType[]>,
    ]);

    await awaitTx(tx).catch(() => {});

    if (!meta || meta.schemaVersion !== SCHEMA_VERSION) {
      return null;
    }

    idbManifest.touchLastAccessed(dbName(spaceId)).catch(() => {});

    return { meta, entities: entities ?? [] };
  } catch (err) {
    log.warn(`[SpaceEntityIDB] readSnapshot(${spaceId}) failed`, err);
    return null;
  } finally {
    db?.close();
  }
}

/**
 * Atomically replace the snapshot for a space (clear stores then bulk put).
 */
export async function writeSnapshot(
  spaceId: string,
  entities: BaseEntityType[],
  dataspaceId: string | null,
): Promise<void> {
  if (!spaceId) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(spaceId);
    const tx = db.transaction([META_STORE, ENTITIES_STORE], 'readwrite');
    const metaStore = tx.objectStore(META_STORE);
    const entitiesStore = tx.objectStore(ENTITIES_STORE);

    entitiesStore.clear();

    for (const entity of entities) {
      if (!entity._id) continue;
      entitiesStore.put(entity);
    }

    const meta: SpaceEntitiesMeta = {
      k: META_KEY,
      etag: null,
      fetchedAt: Date.now(),
      entityCount: entities.length,
      dataspaceId,
      schemaVersion: SCHEMA_VERSION,
    };
    metaStore.put(meta);

    await awaitTx(tx);

    await idbManifest.register(dbName(spaceId), 'space-entities', spaceId);
  } catch (err) {
    log.warn(`[SpaceEntityIDB] writeSnapshot(${spaceId}) failed`, err);
  } finally {
    db?.close();
  }
}

/**
 * Patch a single entity in the cache (used when org Y.Doc node patches arrive).
 */
export async function patchEntity(
  spaceId: string,
  entity: BaseEntityType,
): Promise<void> {
  if (!spaceId || !entity._id) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(spaceId);
    const tx = db.transaction([ENTITIES_STORE], 'readwrite');
    tx.objectStore(ENTITIES_STORE).put(entity);
    await awaitTx(tx);
    idbManifest.touchLastAccessed(dbName(spaceId)).catch(() => {});
  } catch (err) {
    log.warn(`[SpaceEntityIDB] patchEntity(${spaceId}, ${entity._id}) failed`, err);
  } finally {
    db?.close();
  }
}

/**
 * Drop the snapshot for a space (and unregister from the manifest).
 */
export async function deleteSnapshot(spaceId: string): Promise<void> {
  if (!spaceId) return;
  const name = dbName(spaceId);
  await deleteIDBDatabase(name);
  await idbManifest.unregister(name);
}
